import React, { useState, useRef } from 'react';
import { ClipboardList, User, School, Calendar, Download, FileText, CheckCircle2, Info, MapPin, ArrowRight, Plus, Trash2, Printer, Eye } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { motion, AnimatePresence } from 'motion/react';
import { addWatermarkToPdf } from '../lib/pdfUtils';

interface Teacher {
  sn: number;
  masterListNo: string;
  name: string;
  schoolName: string;
  firstJoinDate: string;
  basicPay: string;
  currentSchoolJoinDate: string;
  prevHraRate: string;
  currHraRate: string;
  presentDays: string;
  absentDays: string;
  remarks: string;
}

export const AbsentyView: React.FC = () => {
  const [step, setStep] = useState(1);
  const previewRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    blockName: 'गोपालगंज',
    schoolName: 'उच्च माध्यमिक विद्यालय कटघरवाँ',
    schoolLocation: 'गोपालगंज',
    month: 'मार्च',
    year: '2026',
    teachers: [
      { 
        sn: 1, 
        masterListNo: '', 
        name: 'मनोज कुमार', 
        schoolName: 'उच्च माध्यमिक विद्यालय कटघरवाँ गोपालगंज', 
        firstJoinDate: '23-08-2013', 
        basicPay: '35000', 
        currentSchoolJoinDate: '25-07-2025', 
        prevHraRate: '8%', 
        currHraRate: '10%', 
        presentDays: '31', 
        absentDays: '0', 
        remarks: '' 
      }
    ] as Teacher[]
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleTeacherChange = (index: number, field: keyof Teacher, value: string) => {
    const newTeachers = [...formData.teachers];
    newTeachers[index] = { ...newTeachers[index], [field]: value };
    setFormData({ ...formData, teachers: newTeachers });
  };

  const addTeacher = () => {
    const newSn = formData.teachers.length + 1;
    setFormData({
      ...formData,
      teachers: [
        ...formData.teachers,
        { 
          sn: newSn, 
          masterListNo: '', 
          name: '', 
          schoolName: formData.schoolName + ' ' + formData.schoolLocation, 
          firstJoinDate: '', 
          basicPay: '', 
          currentSchoolJoinDate: '', 
          prevHraRate: '8%', 
          currHraRate: '10%', 
          presentDays: '30', 
          absentDays: '0', 
          remarks: '' 
        }
      ]
    });
  };

  const removeTeacher = (index: number) => {
    if (formData.teachers.length <= 1) return;
    const newTeachers = formData.teachers.filter((_, i) => i !== index).map((t, i) => ({ ...t, sn: i + 1 }));
    setFormData({ ...formData, teachers: newTeachers });
  };

  const generatePDF = async () => {
    if (!previewRef.current) return;
    
    const canvas = await html2canvas(previewRef.current, {
      scale: 1.5, // Slightly reduced scale for better performance/size balance
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });
    
    // Use JPEG instead of PNG for significantly smaller file size
    const imgData = canvas.toDataURL('image/jpeg', 0.85); // 0.85 quality is a good balance
    const pdf = new jsPDF({
      orientation: 'l',
      unit: 'mm',
      format: 'a4',
      compress: true // Enable PDF compression
    });
    
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    
    pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
    addWatermarkToPdf(pdf);
    pdf.save(`Absenty_Report_${formData.month}_${formData.year}.pdf`);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-10">
      <div className="print:hidden">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 md:p-8 text-white">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-md border border-white/30">
                <ClipboardList size={32} />
              </div>
              <div>
                <h2 className="text-2xl font-black tracking-tight uppercase">ABSENTY PAGE GENERATOR</h2>
                <p className="text-orange-100 font-bold text-xs tracking-widest mt-1 uppercase">अनुपस्थिति विवरणी प्रपत्र (Inspired by Official Format)</p>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8">
            {/* Steps Indicator */}
            <div className="flex items-center justify-center mb-10">
              <div className={`flex items-center ${step >= 1 ? 'text-orange-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${step >= 1 ? 'border-orange-600 bg-orange-50' : 'border-gray-300'}`}>1</div>
                <span className="ml-2 font-bold text-sm">Basic Info</span>
              </div>
              <div className="w-12 h-0.5 bg-gray-200 mx-4"></div>
              <div className={`flex items-center ${step >= 2 ? 'text-orange-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${step >= 2 ? 'border-orange-600 bg-orange-50' : 'border-gray-300'}`}>2</div>
                <span className="ml-2 font-bold text-sm">Teacher Data</span>
              </div>
              <div className="w-12 h-0.5 bg-gray-200 mx-4"></div>
              <div className={`flex items-center ${step >= 3 ? 'text-orange-600' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${step >= 3 ? 'border-orange-600 bg-orange-50' : 'border-gray-300'}`}>3</div>
                <span className="ml-2 font-bold text-sm">Preview & Export</span>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-black text-gray-500 uppercase tracking-widest ml-1">प्रखण्ड का नाम (Block Name)</label>
                      <input 
                        name="blockName"
                        type="text"
                        value={formData.blockName}
                        onChange={handleChange}
                        className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-gray-500 uppercase tracking-widest ml-1">विद्यालय का नाम (School Name)</label>
                      <input 
                        name="schoolName"
                        type="text"
                        value={formData.schoolName}
                        onChange={handleChange}
                        className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black text-gray-500 uppercase tracking-widest ml-1">विद्यालय का स्थान (Location/Address)</label>
                      <input 
                        name="schoolLocation"
                        type="text"
                        value={formData.schoolLocation}
                        onChange={handleChange}
                        className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-black text-gray-500 uppercase tracking-widest ml-1">माह (Month)</label>
                        <input 
                          name="month"
                          type="text"
                          value={formData.month}
                          onChange={handleChange}
                          className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-black text-gray-500 uppercase tracking-widest ml-1">वर्ष (Year)</label>
                        <input 
                          name="year"
                          type="text"
                          value={formData.year}
                          onChange={handleChange}
                          className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl font-bold text-gray-800 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="pt-6">
                    <button 
                      onClick={() => setStep(2)}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white py-5 rounded-2xl font-black text-xl shadow-lg shadow-orange-100 transition-all flex items-center justify-center group"
                    >
                      NEXT: TEACHER DETAILS
                      <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-black text-gray-800 uppercase tracking-wider">Teacher Records ({formData.teachers.length})</h3>
                    <button 
                      onClick={addTeacher}
                      className="bg-orange-100 text-orange-600 px-4 py-2 rounded-xl font-bold text-sm flex items-center hover:bg-orange-200 transition-colors"
                    >
                      <Plus size={18} className="mr-1" /> Add Teacher
                    </button>
                  </div>

                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                    {formData.teachers.map((teacher, index) => (
                      <div key={index} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 relative group">
                        <button 
                          onClick={() => removeTeacher(index)}
                          className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">शिक्षक का नाम</label>
                            <input 
                              type="text"
                              value={teacher.name}
                              onChange={(e) => handleTeacherChange(index, 'name', e.target.value)}
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">मास्टर सूची क्रमांक</label>
                            <input 
                              type="text"
                              value={teacher.masterListNo}
                              onChange={(e) => handleTeacherChange(index, 'masterListNo', e.target.value)}
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">BASIC PAY</label>
                            <input 
                              type="text"
                              value={teacher.basicPay}
                              onChange={(e) => handleTeacherChange(index, 'basicPay', e.target.value)}
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">प्रथम योगदान तिथि</label>
                            <input 
                              type="text"
                              value={teacher.firstJoinDate}
                              onChange={(e) => handleTeacherChange(index, 'firstJoinDate', e.target.value)}
                              placeholder="DD-MM-YYYY"
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">वर्तमान विद्यालय योगदान</label>
                            <input 
                              type="text"
                              value={teacher.currentSchoolJoinDate}
                              onChange={(e) => handleTeacherChange(index, 'currentSchoolJoinDate', e.target.value)}
                              placeholder="DD-MM-YYYY"
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">HRA (Old / New)</label>
                            <div className="flex gap-2">
                              <input 
                                type="text"
                                value={teacher.prevHraRate}
                                onChange={(e) => handleTeacherChange(index, 'prevHraRate', e.target.value)}
                                className="w-1/2 px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                              />
                              <input 
                                type="text"
                                value={teacher.currHraRate}
                                onChange={(e) => handleTeacherChange(index, 'currHraRate', e.target.value)}
                                className="w-1/2 px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">उपस्थित / अनुपस्थित</label>
                            <div className="flex gap-2">
                              <input 
                                type="text"
                                value={teacher.presentDays}
                                onChange={(e) => handleTeacherChange(index, 'presentDays', e.target.value)}
                                className="w-1/2 px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                              />
                              <input 
                                type="text"
                                value={teacher.absentDays}
                                onChange={(e) => handleTeacherChange(index, 'absentDays', e.target.value)}
                                className="w-1/2 px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-400 uppercase">अभ्युक्ति (Remarks)</label>
                            <input 
                              type="text"
                              value={teacher.remarks}
                              onChange={(e) => handleTeacherChange(index, 'remarks', e.target.value)}
                              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg font-bold text-sm focus:border-orange-500 outline-none"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pt-6 flex gap-4">
                    <button 
                      onClick={() => setStep(1)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-5 rounded-2xl font-black text-xl transition-all"
                    >
                      BACK
                    </button>
                    <button 
                      onClick={() => setStep(3)}
                      className="flex-[2] bg-orange-600 hover:bg-orange-700 text-white py-5 rounded-2xl font-black text-xl shadow-lg shadow-orange-100 transition-all flex items-center justify-center group"
                    >
                      PREVIEW REPORT
                      <Eye className="ml-3 group-hover:scale-110 transition-transform" />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="space-y-8"
                >
                  <div className="bg-orange-50 p-4 rounded-2xl border border-orange-100 flex items-center space-x-3">
                    <Info size={20} className="text-orange-600 shrink-0" />
                    <p className="text-orange-800 text-xs font-bold">
                      नीचे दिए गए प्रीव्यू को चेक करें। यदि सब सही है तो 'Download PDF' या 'Print' बटन पर क्लिक करें।
                    </p>
                  </div>

                  {/* High Fidelity Preview Section */}
                  <div className="overflow-x-auto bg-gray-100 p-4 md:p-8 rounded-3xl border-2 border-dashed border-gray-300">
                    <div 
                      ref={previewRef}
                      className="bg-white p-8 md:p-12 shadow-2xl mx-auto min-w-[1000px] text-black font-serif"
                      style={{ width: '297mm', minHeight: '210mm' }}
                    >
                      {/* Official Header */}
                      <div className="text-center mb-8 space-y-2">
                        <h1 className="text-3xl font-bold">प्रखण्ड का नाम - {formData.blockName}</h1>
                        <h2 className="text-2xl font-bold">मास्टर सूची (माध्यमिक/उच्च माध्यमिक /प्रधानाध्यापक ,शिक्षक वर्ग 9-10 एवं 11-12 )</h2>
                        <div className="flex justify-between items-end pt-4">
                          <span className="text-xl font-bold">विपत्र -1B</span>
                          <span className="text-xl font-bold">माह का नाम:- {formData.month} {formData.year}</span>
                        </div>
                      </div>

                      {/* Official Table */}
                      <table className="w-full border-collapse border-2 border-black text-[13px]">
                        <thead>
                          <tr>
                            <th className="border border-black p-1 w-10">क्रम संख्या</th>
                            <th className="border border-black p-1 w-20">मास्टर सूची का क्रमांक</th>
                            <th className="border border-black p-1 w-40">शिक्षक का नाम</th>
                            <th className="border border-black p-1 w-48">विद्यालय का नाम</th>
                            <th className="border border-black p-1 w-28">विद्यालय में प्रथम योगदान की तिथि</th>
                            <th className="border border-black p-1 w-20">BASIC PAY</th>
                            <th className="border border-black p-1 w-28">वर्तमान विद्यालय में योगदान तिथि</th>
                            <th className="border border-black p-1 w-24">पूर्व से प्राप्त हो रहा HRA दर</th>
                            <th className="border border-black p-1 w-24">वर्तमान में निर्धारित HRA दर</th>
                            <th className="border border-black p-1 w-20">कुल उपस्थित दिन</th>
                            <th className="border border-black p-1 w-20">कुल अनुपस्थित दिन</th>
                            <th className="border border-black p-1 w-24">अभ्युक्ति</th>
                          </tr>
                          <tr className="bg-gray-50 font-bold text-center">
                            <td className="border border-black p-0.5">1</td>
                            <td className="border border-black p-0.5">2</td>
                            <td className="border border-black p-0.5">3</td>
                            <td className="border border-black p-0.5">4</td>
                            <td className="border border-black p-0.5">5</td>
                            <td className="border border-black p-0.5">6</td>
                            <td className="border border-black p-0.5">7</td>
                            <td className="border border-black p-0.5">8</td>
                            <td className="border border-black p-0.5">9</td>
                            <td className="border border-black p-0.5">10</td>
                            <td className="border border-black p-0.5">11</td>
                            <td className="border border-black p-0.5">12</td>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.teachers.map((teacher, idx) => (
                            <tr key={idx} className="text-center h-10">
                              <td className="border border-black p-1 font-bold">{teacher.sn}</td>
                              <td className="border border-black p-1">{teacher.masterListNo}</td>
                              <td className="border border-black p-1 text-left font-bold px-2">{teacher.name}</td>
                              {idx === 0 && (
                                <td className="border border-black p-1 text-[11px] leading-tight" rowSpan={formData.teachers.length}>
                                  {formData.schoolName}<br/>{formData.schoolLocation}
                                </td>
                              )}
                              <td className="border border-black p-1 font-bold">{teacher.firstJoinDate}</td>
                              <td className="border border-black p-1 font-bold">{teacher.basicPay}</td>
                              <td className="border border-black p-1 font-bold">{teacher.currentSchoolJoinDate}</td>
                              <td className="border border-black p-1 font-bold">{teacher.prevHraRate}</td>
                              <td className="border border-black p-1 font-bold">{teacher.currHraRate}</td>
                              <td className="border border-black p-1 font-bold">{teacher.presentDays}</td>
                              <td className="border border-black p-1 font-bold">{teacher.absentDays}</td>
                              <td className="border border-black p-1">{teacher.remarks}</td>
                            </tr>
                          ))}
                          {/* Fill empty rows if needed to maintain layout */}
                          {formData.teachers.length < 8 && Array.from({ length: 8 - formData.teachers.length }).map((_, i) => (
                            <tr key={`empty-${i}`} className="h-10">
                              <td className="border border-black p-1 font-bold">{formData.teachers.length + i + 1}</td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                              <td className="border border-black p-1"></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Official Footer */}
                      <div className="mt-20 flex justify-end">
                        <div className="text-center space-y-1">
                          <p className="text-xl font-bold">प्रधानाध्यापक</p>
                          <p className="text-xl font-bold">{formData.schoolName}</p>
                          <p className="text-xl font-bold">{formData.schoolLocation}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 flex flex-col md:flex-row gap-4">
                    <button 
                      onClick={() => setStep(2)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-5 rounded-2xl font-black text-xl transition-all"
                    >
                      EDIT DATA
                    </button>
                    <button 
                      onClick={handlePrint}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-2xl font-black text-xl shadow-lg shadow-indigo-100 transition-all flex items-center justify-center"
                    >
                      <Printer className="mr-3" /> PRINT
                    </button>
                    <button 
                      onClick={generatePDF}
                      className="flex-[2] bg-orange-600 hover:bg-orange-700 text-white py-5 rounded-2xl font-black text-xl shadow-lg shadow-orange-100 transition-all flex items-center justify-center group"
                    >
                      <Download className="mr-3 group-hover:scale-110 transition-transform" />
                      DOWNLOAD PDF
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Print-only layout */}
      <div className="hidden print:block bg-white text-black font-serif p-0 m-0">
        <div className="p-4">
          <div className="text-center mb-6 space-y-1">
            <h1 className="text-2xl font-bold">प्रखण्ड का नाम - {formData.blockName}</h1>
            <h2 className="text-xl font-bold">मास्टर सूची (माध्यमिक/उच्च माध्यमिक /प्रधानाध्यापक ,शिक्षक वर्ग 9-10 एवं 11-12 )</h2>
            <div className="flex justify-between items-end pt-2">
              <span className="text-lg font-bold">विपत्र -1B</span>
              <span className="text-lg font-bold">माह का नाम:- {formData.month} {formData.year}</span>
            </div>
          </div>

          <table className="w-full border-collapse border-2 border-black text-[11px]">
            <thead>
              <tr>
                <th className="border border-black p-1 w-8">क्रम संख्या</th>
                <th className="border border-black p-1 w-16">मास्टर सूची का क्रमांक</th>
                <th className="border border-black p-1 w-32">शिक्षक का नाम</th>
                <th className="border border-black p-1 w-40">विद्यालय का नाम</th>
                <th className="border border-black p-1 w-24">विद्यालय में प्रथम योगदान की तिथि</th>
                <th className="border border-black p-1 w-16">BASIC PAY</th>
                <th className="border border-black p-1 w-24">वर्तमान विद्यालय में योगदान तिथि</th>
                <th className="border border-black p-1 w-20">पूर्व से प्राप्त हो रहा HRA दर</th>
                <th className="border border-black p-1 w-20">वर्तमान में निर्धारित HRA दर</th>
                <th className="border border-black p-1 w-16">कुल उपस्थित दिन</th>
                <th className="border border-black p-1 w-16">कुल अनुपस्थित दिन</th>
                <th className="border border-black p-1 w-20">अभ्युक्ति</th>
              </tr>
              <tr className="bg-gray-50 font-bold text-center">
                <td className="border border-black p-0.5">1</td>
                <td className="border border-black p-0.5">2</td>
                <td className="border border-black p-0.5">3</td>
                <td className="border border-black p-0.5">4</td>
                <td className="border border-black p-0.5">5</td>
                <td className="border border-black p-0.5">6</td>
                <td className="border border-black p-0.5">7</td>
                <td className="border border-black p-0.5">8</td>
                <td className="border border-black p-0.5">9</td>
                <td className="border border-black p-0.5">10</td>
                <td className="border border-black p-0.5">11</td>
                <td className="border border-black p-0.5">12</td>
              </tr>
            </thead>
            <tbody>
              {formData.teachers.map((teacher, idx) => (
                <tr key={idx} className="text-center h-8">
                  <td className="border border-black p-1 font-bold">{teacher.sn}</td>
                  <td className="border border-black p-1">{teacher.masterListNo}</td>
                  <td className="border border-black p-1 text-left font-bold px-2">{teacher.name}</td>
                  {idx === 0 && (
                    <td className="border border-black p-1 text-[10px] leading-tight" rowSpan={formData.teachers.length}>
                      {formData.schoolName}<br/>{formData.schoolLocation}
                    </td>
                  )}
                  <td className="border border-black p-1 font-bold">{teacher.firstJoinDate}</td>
                  <td className="border border-black p-1 font-bold">{teacher.basicPay}</td>
                  <td className="border border-black p-1 font-bold">{teacher.currentSchoolJoinDate}</td>
                  <td className="border border-black p-1 font-bold">{teacher.prevHraRate}</td>
                  <td className="border border-black p-1 font-bold">{teacher.currHraRate}</td>
                  <td className="border border-black p-1 font-bold">{teacher.presentDays}</td>
                  <td className="border border-black p-1 font-bold">{teacher.absentDays}</td>
                  <td className="border border-black p-1">{teacher.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-16 flex justify-end">
            <div className="text-center space-y-1">
              <p className="text-lg font-bold">प्रधानाध्यापक</p>
              <p className="text-lg font-bold">{formData.schoolName}</p>
              <p className="text-lg font-bold">{formData.schoolLocation}</p>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body * { visibility: hidden; }
          .print\\:block, .print\\:block * { visibility: visible; }
          .print\\:block { position: absolute; left: 0; top: 0; width: 100%; }
          @page { size: landscape; margin: 10mm; }
        }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}} />
    </div>
  );
};
